import React from 'react';
import { StatusResponse } from '../types';
import { Activity, Clock, MapPin, Users, StopCircle, CheckCircle } from 'lucide-react';

interface StatusDisplayProps {
  status: StatusResponse | null;
  onStopMonitoring: () => void;
  isLoading: boolean;
}

export const StatusDisplay: React.FC<StatusDisplayProps> = ({
  status,
  onStopMonitoring,
  isLoading,
}) => {
  if (!status || !status.is_running) {
    return null;
  }

  const formatDateTime = (isoString: string) => {
    return new Date(isoString).toLocaleString();
  };

  // Check if there's a mismatch between expected and actual status
  const threadIssue = status.in_memory_status && !status.thread_alive;

  return (
    <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-xl border border-green-200 overflow-hidden">
      <div className={`bg-gradient-to-r px-6 py-4 ${
        threadIssue 
          ? 'from-yellow-600 to-orange-600' 
          : 'from-green-600 to-green-700'
      }`}>
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <Activity className={`w-5 h-5 ${threadIssue ? 'animate-bounce' : 'animate-pulse'}`} />
            {threadIssue ? 'Monitoring Restarting...' : 'Monitoring Active'}
          </h3>
          <div className="flex items-center gap-2">
            <CheckCircle className={`w-4 h-4 ${threadIssue ? 'text-yellow-200' : 'text-green-200'}`} />
            <span className={`text-sm ${threadIssue ? 'text-yellow-100' : 'text-green-100'}`}>
              {threadIssue ? 'Syncing' : 'Live'}
            </span>
          </div>
        </div>
        {threadIssue && (
          <p className="text-yellow-100 text-sm mt-1">
            Thread status syncing... Monitoring will resume shortly.
          </p>
        )}
      </div>

      <div className="p-6">
        {status.config && (
          <div className="space-y-4">
            {/* Route Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center gap-3 p-3 bg-white rounded-lg border">
                <MapPin className="w-5 h-5 text-blue-500" />
                <div>
                  <p className="text-sm text-gray-600">Route</p>
                  <p className="font-semibold">{status.config.start_place} → {status.config.end_place}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3 p-3 bg-white rounded-lg border">
                <Clock className="w-5 h-5 text-purple-500" />
                <div>
                  <p className="text-sm text-gray-600">Journey Date</p>
                  <p className="font-semibold">{status.config.journey_date}</p>
                </div>
              </div>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-lg border p-4">
              <h4 className="font-medium text-gray-900 mb-3">Active Filters</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                {status.config.required_seats && status.config.required_seats.length > 0 && (
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-orange-500" />
                    <span className="text-gray-600">Seats:</span>
                    <span className="font-medium">{status.config.required_seats.join(', ')}</span>
                  </div>
                )}
                
                {(status.config.from_hour !== undefined || status.config.to_hour !== undefined) && (
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-indigo-500" />
                    <span className="text-gray-600">Time:</span>
                    <span className="font-medium">
                      {status.config.from_hour !== undefined ? `${status.config.from_hour}:00` : 'Any'} - {status.config.to_hour !== undefined ? `${status.config.to_hour}:00` : 'Any'}
                    </span>
                  </div>
                )}
                
                <div className="flex items-center gap-2">
                  <Activity className="w-4 h-4 text-green-500" />
                  <span className="text-gray-600">Check every:</span>
                  <span className="font-medium">{status.config.sleep_duration}s</span>
                </div>
              </div>
            </div>

            {/* Started Time */}
            {status.config.started_at && (
              <div className="flex items-center gap-2 text-sm text-gray-600 bg-white p-3 rounded-lg border">
                <Clock className="w-4 h-4" />
                <span>Started: {formatDateTime(status.config.started_at)}</span>
              </div>
            )}

            {/* Stop Button */}
            <button
              onClick={onStopMonitoring}
              disabled={isLoading}
              className={`w-full py-3 px-4 rounded-lg font-medium transition-all duration-200 flex items-center justify-center gap-2 ${
                isLoading
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-red-600 hover:bg-red-700 hover:-translate-y-0.5 hover:shadow-lg'
              } text-white`}
            >
              {isLoading ? (
                <>
                  <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
                  Stopping...
                </>
              ) : (
                <>
                  <StopCircle className="w-4 h-4" />
                  Stop Monitoring
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}; 